import './styles/demo.css';
import BlogsList from './components/BlogsList';
import React from 'react';
import ReactDOM from 'react-dom/client';
import {ArticleViewer} from './components/ArticleViewer';
import {ProfileEditor} from './components/ProfileEditor';
import {ProfilePageDemo} from './components/ProfilePage';
import Dashboard from './components/Dashboard';
import PerformanceAnalytics from './components/PerformanceAnalytics';
import OverView from './components/OverView';
import {DesignerPreview, PublicProfile, MyPages, BlockNoteDesigner, BlockNoteDesignerPreview, BlockNotePublicProfile} from './components/Designer';
import Brands from './components/Designer/Brands';
import ProductGallery from './components/Designer/ProductGallery';
import OrdersList from './components/OrdersList';
import TransactionsList from './components/TransactionsList';
import PublicGlobe from './components/PublicGlobe';
import {AccountSettings} from './components/Settings';
import {Toaster} from './components/Toast';
import {AuthProvider, LoginPage, SignupPage, ProtectedRoute} from './components/Auth';
import {Sidebar} from './components/Layout';
import {
    Route,
    BrowserRouter as Router,
    Routes
} from 'react-router-dom';
import Template from './components/Template';

// Layout wrapper with Sidebar
function SidebarLayout({children}) {
    return (
        <Sidebar>
            {children}
        </Sidebar>
    );
}

// Check for shared article view parameter
function AppWrapper() {
    const urlParams = new URLSearchParams(window.location.search);
    const viewArticleId = urlParams.get('view');

    // If viewing a shared article, render the ArticleViewer
    if (viewArticleId) {
        return (
            <AuthProvider>
                <ArticleViewer articleId={viewArticleId} />
            </AuthProvider>
        );
    }

    // Otherwise render the normal app with routing
    return (
        <AuthProvider>
            <Toaster />
            <Router>
                <Routes>
                    {/* Public routes - no authentication required */}
                    <Route element={<LoginPage />} path="/login" />
                    <Route element={<SignupPage />} path="/register" />
                    <Route element={<PublicProfile />} path="/p" />

                    {/* Public Globe - Embeddable analytics globe */}
                    <Route element={<PublicGlobe />} path="/embed/globe" />

                    {/* Protected routes with Sidebar - require authentication */}
                    {/* Dashboard - Default route after login */}
                    <Route element={
                        <ProtectedRoute>
                            <SidebarLayout>
                                <Dashboard />
                            </SidebarLayout>
                        </ProtectedRoute>
                    } path="/dashboard" />

                    {/* Default route - redirect to dashboard */}
                    <Route element={
                        <ProtectedRoute>
                            <SidebarLayout>
                                <Dashboard />
                            </SidebarLayout>
                        </ProtectedRoute>
                    } path="/" />

                    {/* Performance Analytics - Static analytics page (kept but not linked from sidebar) */}
                    <Route element={
                        <ProtectedRoute>
                            <SidebarLayout>
                                <PerformanceAnalytics />
                            </SidebarLayout>
                        </ProtectedRoute>
                    } path="/analytics" />

                    {/* Financial Dashboard - New dark-style financial view */}
                    <Route element={
                        <ProtectedRoute>
                            <SidebarLayout>
                                <OverView />
                            </SidebarLayout>
                        </ProtectedRoute>
                    } path="/financial-dashboard" />

                    {/* My Pages - Profile Listing */}
                    <Route element={
                        <ProtectedRoute>
                            <SidebarLayout>
                                <MyPages />
                            </SidebarLayout>
                        </ProtectedRoute>
                    } path="/my-pages" />

                    {/* Settings - Account Settings */}
                    <Route element={
                        <ProtectedRoute>
                            <SidebarLayout>
                                <AccountSettings onBackToDashboard={() => window.location.href = '/dashboard'} />
                            </SidebarLayout>
                        </ProtectedRoute>
                    } path="/settings" />

                    {/* Designer - Bio Profile Builder (BlockNote-based) */}
                    <Route element={
                        <ProtectedRoute>
                            <SidebarLayout>
                                <BlockNoteDesigner />
                            </SidebarLayout>
                        </ProtectedRoute>
                    } path="/designer" />

                    <Route element={
                        <ProtectedRoute>
                            <SidebarLayout>
                                <Template />
                            </SidebarLayout>
                        </ProtectedRoute>
                    } path="/template" />

                    {/* Brands */}
                    <Route element={
                        <ProtectedRoute>
                            <SidebarLayout>
                                <Brands />
                            </SidebarLayout>
                        </ProtectedRoute>
                    } path="/brands" />

                    {/* Orders & Transactions (Shopify) */}
                    <Route element={
                        <ProtectedRoute>
                            <SidebarLayout>
                                <OrdersList />
                            </SidebarLayout>
                        </ProtectedRoute>
                    } path="/orders" />
                    <Route element={
                        <ProtectedRoute>
                            <SidebarLayout>
                                <TransactionsList />
                            </SidebarLayout>
                        </ProtectedRoute>
                    } path="/transactions" />

                    {/* Product Gallery - Detail page for a brand */}
                    <Route element={
                        <ProtectedRoute>
                            <SidebarLayout>
                                <ProductGallery />
                            </SidebarLayout>
                        </ProtectedRoute>
                    } path="/product-gallery/:brandId" />

                    {/* Designer Preview - Full screen without sidebar */}
                    <Route element={
                        <ProtectedRoute>
                            <DesignerPreview />
                        </ProtectedRoute>
                    } path="/designer/preview" />

                    {/* BlockNote Designer Preview */}
                    <Route element={
                        <ProtectedRoute>
                            <BlockNoteDesignerPreview />
                        </ProtectedRoute>
                    } path="/blocknote-designer/preview" />

                    {/* Blog Routes */}
                    <Route element={
                        <ProtectedRoute>
                            <SidebarLayout>
                                <BlogsList />
                            </SidebarLayout>
                        </ProtectedRoute>
                    } path="/blogs" />

                    {/* Profile Routes */}
                    <Route element={
                        <ProtectedRoute>
                            <SidebarLayout>
                                <ProfilePageDemo />
                            </SidebarLayout>
                        </ProtectedRoute>
                    } path="/profile" />

                    <Route element={
                        <ProtectedRoute>
                            <SidebarLayout>
                                <ProfileEditor />
                            </SidebarLayout>
                        </ProtectedRoute>
                    } path="/profile/edit" />

                    {/* Public profile by slug (mydomain/profile-slug) – must be last */}
                    <Route element={<BlockNotePublicProfile />} path="/:slug" />
                </Routes>
            </Router>
        </AuthProvider>
    );
}

ReactDOM.createRoot(document.getElementById('root')).render(
    <React.StrictMode>
        <AppWrapper />
    </React.StrictMode>
);
